import React, { useMemo } from 'react';

import formatNumber from 'helpers/formatNumber';
import useCustomerName from '../util/useCustomerName';
import getPlaylistKeyValueGroupItems from '../util/getPlaylistKeyValueGroupItems';

import Box from 'components/elements/Box';
import DoubleChart from 'components/data-visualization/DoubleChart/DoubleChart';
import TippyToolTip from 'components/base/TippyToolTip/TippyToolTip';

import { IPlaylistGraphProps } from './PlaylistDesktopExpandedRow';
import { IPlaylistItem, noop } from './PlaylistTableRow';
import {
  TableRow,
  Cell,
  CellContent,
  ChangeArrow,
  StyledCaret,
  StyledCaretWrap,
  Metric,
  MetaDetails,
  MetricsList,
  MobileExpandedCell,
  ExpandedMetrics,
  PositionCell,
  EntityTitle,
  EntityMeta,
  ExpandedCellContent,
} from '../styles/PlaylistTableMobileRow.styles';
import { EntityMetaData } from '../styles/PlaylistTableRow.style';
import CountryFlag from 'components/base/CountryFlag/index';

import { ITableHeader } from 'components/data-containers/PlaylistTable/util/getMenuItems';

interface IPlaylistTableMobileRowProps extends IPlaylistGraphProps {
  playlist: IPlaylistItem;
  expandedId: number | null;
  expandedCountryId: string | null;
  toggleExpand: (id: number, displayName: string, countryId?: string) => any;
  isTopList: boolean;
  priorityPlaylistsShown?: boolean;
  setPlaylistEditing: any;
  headers: ITableHeader[];
  canNotAnnotate: boolean;
}

const PlaylistTableMobileRow: React.FC<IPlaylistTableMobileRowProps> = (
  props
) => {
  const {
    playlist,
    expandedId,
    expandedCountryId,
    toggleExpand,
    playlistData,
    isTopList,
    priorityPlaylistsShown,
    setPlaylistEditing,
    headers,
    canNotAnnotate,
    ...rest
  } = props;
  const getChangeArrow = (isPositive: boolean | null): any =>
    isPositive === null ? ' ' : isPositive ? '▲' : '▼';

  const isPositiveChange = playlist?.stream_wow_in_progress > 0;
  const customerName = useCustomerName(playlist);
  const { dateAdded, lastPeakDate, peak, totalDays } = playlist;
  const playlistKeyValueGroupItems: any[] = useMemo(
    () =>
      getPlaylistKeyValueGroupItems({
        isTopList,
        headers,
        dateAdded,
        lastPeakDate,
        peak,
        totalDays,
        customerName,
        playlist,
      }),
    [isTopList, headers, dateAdded, lastPeakDate, peak, totalDays, customerName]
  );
  const { playlistId, displayName, countryId } = playlist;
  const isExpanded =
    expandedId === playlistId && expandedCountryId === countryId;

  const markPriority = (event: any): void => {
    event.stopPropagation();
    if (playlist?.playlistType === 'priority') return;
    setPlaylistEditing(
      playlist?.priority
        ? null
        : { name: playlist?.displayName, id: playlist?.playlistId }
    );
  };

  let priorityType = '';
  let tooltipContent = `Is this a noteworthy playlist? 
  Click to change to a Priority Playlist for all users.`;
  if (playlist?.playlistType === 'priority') {
    if (playlist?.activity === 'current') {
      priorityType = 'disc';
      tooltipContent = 'This is a Priority Playlist (according to WMG staff)';
    }
    if (playlist?.activity === 'requested') {
      priorityType = 'rhombus';
      tooltipContent = `Priority status for this playlist has been requested.
      When it is set as Priority it will turn blue.`;
    }
  }

  return (
    <>
      <TableRow
        isExpanded={isExpanded}
        mainExpandedRow={isExpanded}
        onClick={toggleExpand(playlistId, displayName, countryId)}
      >
        {isTopList ? (
          <>
            <Cell
              data-cell-value={playlist.newTrackCount}
              cellBasis="90%"
              justifyContent="flex-start"
            >
              <EntityTitle paddingLeft>{playlist.displayName}</EntityTitle>
            </Cell>
            <Cell open={isExpanded}>
              <StyledCaretWrap>
                <StyledCaret
                  open={isExpanded}
                  onClick={toggleExpand(playlistId, displayName, countryId)}
                />
              </StyledCaretWrap>
            </Cell>
          </>
        ) : (
          <>
            <Cell>
              <ChangeArrow isStretched={true} isPositive={isPositiveChange}>
                {getChangeArrow(isPositiveChange)}
              </ChangeArrow>
            </Cell>

            <PositionCell>{formatNumber(playlist.position)}</PositionCell>

            <CellContent>
              {priorityPlaylistsShown && (
                <Box
                  display={canNotAnnotate ? 'none' : 'flex'}
                  justifyContent="center"
                  minWidth="60px"
                  paddingTop="3px"
                >
                  <TippyToolTip
                    tooltipContent={tooltipContent}
                    maxWidth="330px"
                    enabled
                  >
                    <span
                      className={
                        playlist?.playlistType === 'priority'
                          ? priorityType
                          : 'bordered-plus'
                      }
                      onClick={!canNotAnnotate ? markPriority : noop}
                    >
                      {playlist?.playlistType === 'non_priority' && '+'}
                    </span>
                  </TippyToolTip>
                </Box>
              )}

              <EntityMetaData>
                <EntityTitle>{playlist.displayName}</EntityTitle>
                <EntityMeta>Owned by {playlist.ownerDisplayName}</EntityMeta>
              </EntityMetaData>
            </CellContent>

            <Cell open={isExpanded}>
              <StyledCaretWrap>
                <StyledCaret
                  open={isExpanded}
                  onClick={toggleExpand(playlistId, displayName, countryId)}
                />
              </StyledCaretWrap>
            </Cell>
          </>
        )}
      </TableRow>

      {isExpanded && (
        <TableRow isExpanded={isExpanded}>
          <MobileExpandedCell>
            <ExpandedCellContent>
              <ExpandedMetrics>
                <Box display="flex" alignItems="center">
                  <MetricsList key={playlist.displayName + '_key'}>
                    {playlistKeyValueGroupItems.map((item: any) => {
                      const isRawValue = item.type === 'raw';
                      const amount = isRawValue
                        ? item.metric
                        : (playlist as any)[item.metric.key];
                      const isPercent =
                        item.type === 'trend' || item.type === 'percentage';
                      const isFull = !isPercent;
                      const value = isRawValue
                        ? item.metric
                        : formatNumber(
                            amount,
                            isFull ? 'full' : item.type,
                            isPercent
                          );
                      const headerHasTerritory = headers.find(
                        (header: any) => header.value === 'countryId'
                      );

                      return item?.metric?.value === 'countryId' ? (
                        headerHasTerritory && (
                          <li key={item.label + '_li'}>
                            <Box display="flex" alignItems="center">
                              <CountryFlag
                                small
                                id={playlist?.playlistTerritory?.id ?? 'WW'}
                                displayName={
                                  playlist?.playlistTerritory?.displayName
                                }
                                flagImgSmall={
                                  playlist?.playlistTerritory?.flagImgSmall ||
                                  ''
                                }
                                flagImgMedium={
                                  playlist?.playlistTerritory?.flagImgMedium ||
                                  ''
                                }
                                flagImgLarge={
                                  playlist?.playlistTerritory?.flagImgLarge ||
                                  ''
                                }
                              />
                              <Metric isTopList={isTopList}>
                                {playlist?.playlistTerritory?.displayName}
                              </Metric>
                            </Box>
                          </li>
                        )
                      ) : (
                        <li key={item.label + '_li'}>
                          <Box display="flex">
                            <MetaDetails isTopList={isTopList} isUpperCase>
                              {item.label}
                            </MetaDetails>
                            <Metric isTopList={isTopList}>{value}</Metric>
                          </Box>
                        </li>
                      );
                    })}
                  </MetricsList>
                </Box>
              </ExpandedMetrics>
            </ExpandedCellContent>
            {isTopList ? null : (
              <DoubleChart {...rest} positionsData={playlistData} />
            )}
          </MobileExpandedCell>
        </TableRow>
      )}
    </>
  );
};

export default PlaylistTableMobileRow;
